/**
 * buildRentalCalcRequest — P0-1 (7-kör FE-2 slice-2) store → BE-calc kérés-mapper.
 *
 * A wizard-store render-state-jét (cart + dátumok + óradíj-params + tartozék-katalógus)
 * a `POST /rentals/calculate` (#501) `RentalCalcRequest` DTO-jára képezi le, amit a
 * `useRentalCalcPricing` hook a V2-sidebar BE-authoritative árazásához fogyaszt.
 *
 * Pure fn (0 store-függés) → izoláltan tesztelhető + a sidebar-render mellékhatás-mentes.
 * `null`-t ad, ha a kérés értelmetlen (üres cart vagy hiányzó dátum) — ekkor a hook nem hív.
 *
 * Tartozék-leképzés a `wizard-store.calculateCartPricing` loop-mintáját követi
 * (`wizard-store.ts:517`): qty>0 szűrés, kind→category, RENTABLE→dailyRate, CONSIGNMENT→price.
 */
import type {
  RentalCalcAccessory,
  RentalCalcItem,
  RentalCalcRequest,
} from '@kgc/rental-core/rental-dto';
import type { CartItem } from '../types';
import type { AccessoryCatalogQtyEntry } from '../wizard-store';

export interface BuildRentalCalcRequestParams {
  cart: CartItem[];
  startDate: Date | null;
  endDate: Date | null;
  useHourlyBilling?: boolean;
  startTime?: string | null;
  endTime?: string | null;
  accessoryCatalogQtys?: Map<string, Record<string, AccessoryCatalogQtyEntry>>;
}

export function buildRentalCalcRequest(p: BuildRentalCalcRequestParams): RentalCalcRequest | null {
  const { cart, startDate, endDate } = p;
  if (cart.length === 0 || !startDate || !endDate) return null;

  const items: RentalCalcItem[] = cart.map(ci => {
    const eq = ci.equipment;
    const item: RentalCalcItem = {
      equipmentId: eq.id,
      quantity: ci.quantity,
      dailyRate: eq.dailyRate,
      weeklyRate: eq.weeklyRate,
      monthlyRate: eq.monthlyRate,
      depositAmount: eq.depositAmount,
    };
    // Az óradíj opcionális a sémában — csak akkor küldjük, ha a gépnek van rátája.
    if (eq.hourlyRate != null) item.hourlyRate = eq.hourlyRate;
    return item;
  });

  const accessories: RentalCalcAccessory[] = [];
  if (p.accessoryCatalogQtys) {
    for (const [equipmentId, record] of p.accessoryCatalogQtys) {
      for (const [accessoryId, entry] of Object.entries(record)) {
        if (entry.qty <= 0) continue;
        const acc: RentalCalcAccessory = {
          equipmentId,
          accessoryId,
          category: entry.kind,
          quantity: entry.qty,
        };
        if (entry.kind === 'RENTABLE') acc.dailyRate = entry.dailyRate;
        if (entry.kind === 'CONSIGNMENT' && entry.price != null) acc.price = entry.price;
        accessories.push(acc);
      }
    }
  }

  const request: RentalCalcRequest = { items, startDate, endDate };
  if (accessories.length > 0) request.accessories = accessories;
  // Óradíjas mód: csak teljes (flag + mindkét időpont) esetén küldünk idő-mezőt
  // (a séma HH:MM regexet vár — részleges állapot 400-at adna).
  if (p.useHourlyBilling && p.startTime && p.endTime) {
    request.useHourlyBilling = true;
    request.startTime = p.startTime;
    request.endTime = p.endTime;
  }
  return request;
}
